import React from "react";
import { AgGridReact } from "ag-grid-react";
import { Paper, Box } from "@mui/material";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import TablePagination from "./TableGridPagination";

import { useSelector, useDispatch } from "react-redux";

import {
  EntityType,
  areaOfActivity,
  UserType,
  OrganizationTab,
  RegistrationStatus,
  ComponentType,
} from "../../../common/enum";
import { getEntitiesList } from "../adminSlice";
import "./Style.css";
import { getManagerInviteList } from "../../../redux/features/authSlice";

const EntityTable = ({ t, component, columnDefs, elevation }) => {
  const dispatch = useDispatch();
  const {
    adminDashboard: {
      entitiesList,
      entitiesCount,
      entitiesLimit,
      entitiesOffset,
    },
    auth: {
      managerInviteCount,
      managerInviteOffset,
      managerInviteLimit,
      managerInviteEntities,
      managerInvitePage,
      profile: { entity },
    },
  } = useSelector((state) => state);

  React.useEffect(() => {
    if (component === OrganizationTab.ALL) {
      const data = {
        limit: entitiesLimit,
        offset: entitiesOffset,
      };
      dispatch(getEntitiesList(data));
    } else if (component === OrganizationTab.PENDING) {
      const data = {
        status: RegistrationStatus.pendingApproval,
        limit: entitiesLimit,
        offset: entitiesOffset,
      };
      dispatch(getEntitiesList(data));
    } else if (component === ComponentType.MANAGE_MANGERS) {
      const data = {
        limit: managerInviteLimit,
        offset: managerInviteOffset,
        id: entity.id,
      };
      dispatch(getManagerInviteList(data));
    }
  }, [component, dispatch]);

  const gridRef = React.useRef();

  const entitiesRowData = React.useMemo(() => {
    if (entitiesList?.length > 0) {
      return entitiesList.map((item) => {
        let companyType =
          item.entity.parentEntityId === null ? "Individual" : "child";
        return {
          id: item.entity.id,
          entityId: item.entity.entityId,
          name: item.entity.entityName,
          type: t(
            `${
              Object.keys(UserType)[
                Object.values(UserType).indexOf(item.userType)
              ]
            }`
          ),
          areaOfActivity: t(
            `${
              Object.keys(areaOfActivity)[
                Object.values(areaOfActivity).indexOf(
                  item.entity.areaOfActivity
                )
              ]
            }`
          ),
          entityType: t(
            `${
              Object.keys(EntityType)[
                Object.values(EntityType).indexOf(item.entity.entityType)
              ]
            }`
          ),
          district: item.addresses.length > 0 && item.addresses[0].district,
          county: item.addresses.length > 0 && item.addresses[0].county,
          subDivision:
            item.addresses.length > 0 && item.addresses[0].subDivision,
          addresses: item.addresses.length > 0 ? item.addresses : [],
          contact: item.contact.length > 0 ? item.addresses : [],
          totalDonation: item.totalNumberOfDonations,
          totalCollection: item.totalNumberOfCollections,
          totalChild: item.totalNumberOfChilds,
          isParentEntity:
            item.entity.isParentEntity === true ? "Parent" : companyType,
          status: item.status,
          createdDate: item.createdDate,
        };
      });
    }
    return [];
  }, [entitiesList]);

  const managersRowData = React.useMemo(() => {
    if (managerInviteEntities?.length > 0) {
      return managerInviteEntities.map((item) => {
        return {
          id: item.id,
          inviteToken: item.inviteToken,
          tokenExpiry: item.tokenExpiry,
          status: item.status,
          entityPrimaryId: item.entityPrimaryId,
          email: item.email,
        };
      });
    }
    return [];
  }, [managerInviteEntities]);

  const rowData = React.useMemo(() => {
    if (component === ComponentType.MANAGE_MANGERS) {
      return managersRowData;
    }
    return entitiesRowData;
  }, [component, managerInviteEntities, entitiesList]);

  const defaultColDef = React.useMemo(() => ({
    sortable: true,
    flex: 1,
    minWidth: 100,
    filter: false,
    resizable: true,
  }));
  const rowHeight = 50;

  return (
    <Paper elevation={elevation} sx={{ borderRadius: "20px" }}>
      <div className="ag-theme-alpine" style={{ width: "100%", height: 600 }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          animateRows={true}
          defaultColDef={defaultColDef}
          showToolPanel={false}
          floatingFilter={false}
          pivotPanelShow={"always"}
          paginationPageSize={20}
          rowHeight={rowHeight}
          headerHeight={60}
        />
      </div>
      <Box>
        <TablePagination
          count={
            component === ComponentType.MANAGE_MANGERS
              ? managerInviteCount
              : entitiesCount
          }
          component={component}
        />
      </Box>
    </Paper>
  );
};

export default EntityTable;
